"use client";

/* eslint-disable @next/next/no-img-element -- static Figma vectors at their exact intrinsic sizes */

import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { cn } from "@/lib/utils";
import { Artboard, prefersReducedMotion } from "@/components/trademania-2/shared";

import grain01 from "@/public/trademania-2/k1-grain01.png";
import grain from "@/public/trademania-2/k1-grain.png";
import noise from "@/public/trademania-2/k1-noise.png";
import dotGrid from "@/public/trademania-2/k1-dotgrid.png";
import chevronDown from "@/public/trademania-2/ic-chevron-down.svg";
import maximize from "@/public/trademania-2/ic-maximize.svg";
import aiLogo from "@/public/trademania-2/ai-logo-66.svg";
import aiOrb from "@/public/trademania-2/ai-orb-30.svg";
import idea from "@/public/trademania-2/ic-idea-w.svg";
import addLarge from "@/public/trademania-2/ic-add.svg";
import microphone from "@/public/trademania-2/ic-mic.svg";
import arrowUp from "@/public/trademania-2/ic-arrow-up.svg";
import lineA from "@/public/trademania-2/k1-line-a.svg";
import lineB from "@/public/trademania-2/k1-line-b.svg";
import markerDot from "@/public/trademania-2/k1-dot.svg";
import pointer from "@/public/trademania-2/k1-pointer.svg";
import pointDot from "@/public/trademania-2/k1-dot2.svg";

/* ---------------------------------------------------------------- data */

// volume bars under the chart: [height px (null = full 16.743), opacity, fixed width px]
const BARS: [number | null, number, number?][] = [
  [null, 0.1], [null, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1],
  [null, 0.1], [6.599, 0.1], [6.599, 0.1], [null, 0.1], [null, 0.1], [16.969, 0.1], [16.969, 0.1],
  [28.282, 0.1], [33.938, 0.1], [28.282, 0.1], [16.969, 0.1], [16.969, 0.1], [null, 0.1], [8.484, 0.1],
  [8.484, 0.1], [13.198, 0.1], [13.198, 0.1], [13.198, 0.1], [13.198, 0.1], [null, 0.1], [null, 0.1],
  [32.995, 0.1], [32.995, 0.1], [28.282, 0.1, 2.828], [28.282, 0.6, 2.828], [28.282, 0.6, 3.771],
  [null, 0.6], [37.709, 0.6], [32.053, 0.6], [25.453, 0.6], [25.453, 0.6], [25.453, 0.6], [null, 0.6],
  [25.453, 0.6], [9.427, 0.6], [9.427, 0.6], [9.427, 0.6], [25.453, 0.6], [25.453, 0.6], [15.084, 0.6],
  [15.084, 0.6], [15.084, 0.6], [21.683, 0.6], [21.683, 0.6], [21.683, 0.6], [21.683, 0.6], [null, 0.6],
  [26.396, 0.6], [null, 0.6], [31.11, 0.6], [31.11, 0.6], [31.11, 0.6], [null, 0.1], [null, 0.1],
  [null, 0.1], [25.453, 0.1], [25.453, 0.1], [11.313, 0.1], [17.912, 0.1], [17.912, 0.1], [17.912, 0.1],
  [11.313, 0.1], [11.313, 0.1], [25.453, 0.1], [null, 0.1], [null, 0.1], [null, 0.1], [33.938, 0.1],
  [33.938, 0.1], [33.938, 0.1], [14.141, 0.1], [14.141, 0.1], [50.907, 0.1], [14.141, 0.1], [33.938, 0.1],
  [null, 0.1], [null, 0.1], [null, 0.1], [37.709, 0.1], [37.709, 0.1], [8.484, 0.1], [8.484, 0.1],
  [37.709, 0.1], [24.511, 0.1], [12.255, 0.1], [24.511, 0.1], [24.511, 0.1], [12.255, 0.1], [12.255, 0.1],
  [37.709, 0.1], [59.391, 0.1], [37.709, 0.1], [null, 0.1], [null, 0.1], [null, 0.1], [null, 0.1],
  [null, 0.1], [null, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1], [12.255, 0.1],
  [12.255, 0.1], [12.255, 0.1], [null, 0.1], [null, 0.1], [null, 0.1], [8.484, 0.1], [8.484, 0.1],
  [8.484, 0.1], [null, 0.1], [null, 0.1],
];

const MONTHS = ["Jan", "MAR", "MAI", "JUL", "SEP", "OKT", "DEZ"];
const TRADES: [number, string][] = [
  [243.89, "+$243,89"],
  [-42.28, "-$42,28"],
  [97.96, "+$97,96"],
  [4.28, "+$4,28"],
];
const PRICE = 5150245;
const TRADE_COUNT = 28;
const PROMPT = "Analysiere den Chart";
const GREETING = ["Willkommen", "Julian"];
const DOT_OPACITY = [0.3, 0.6, 1]; // storyboard A5 frame
const THINK = 1.4; // A5 loader runs this long before the answer streams in
const ANSWER_LINES = [
  "Chart analysiert.",
  "Starker Aufwärtsdruck erkannt",
  "Momentum spricht aktuell für Long",
];

type PanelId = "chat" | "chart" | "journal";

const PANELS: PanelId[] = ["chat", "chart", "journal"];
// the carousel track: the set twice, so the card leaving on the left is never the one that has
// to come in on the right. Off-board cards wrap from the left end to the right end unseen.
const TRACK: PanelId[] = [...PANELS, ...PANELS];
const FIRST = 1; // state 01 (Figma 12857:5836): chat · chart · journal

// [width, height] in the centre (storyboard 12849:193614). Cards always render at this size and
// the side ones are scaled down to SIDE_W, so nothing re-flows while they move.
const SIZE: Record<PanelId, [number, number]> = {
  chat: [454, 395],
  chart: [464, 395],
  journal: [454, 396],
};
const SIDE_W = 404;

const BOARD_W = 683;
const BOARD_H = 511;
const GAP = 12;

/* ---------------------------------------------------------------- timing (s) */

const SLIDE = 0.9; // carousel step, right → left
const SLIDE_EASE = "power3.inOut";
const START = 0.3; // inner animation starts after the card reaches the centre
// each card stays until its own inner animation has finished, then holds the last frame this long
const HOLD: Record<PanelId, number> = { chat: 1.8, chart: 1.5, journal: 1.5 };
const RESET = 0.3; // animated parts return to their start while the card travels in
// side cards read at 70%: a board-coloured veil rather than real opacity keeps the cards solid
const SIDE_VEIL = 0.3;

type PanelHandle = {
  /** Fade the animated parts to their start state (the card is on its way to the centre). */
  reset: () => void;
  /** Run the inner animation; returns its length in seconds. */
  play: () => number;
  /** Leave the centre: settle on the resting design. */
  rest: () => void;
};

/**
 * Shared bookkeeping for a panel: `build` returns the paused inner timeline, `resetTo`
 * the start-state timeline. Resting = the inner timeline's last frame.
 */
function usePanelTimeline(build: () => gsap.core.Timeline, resetTo: () => gsap.core.Timeline, extraKill?: () => void) {
  const tl = useRef<gsap.core.Timeline | null>(null);
  const rewind = useRef<gsap.core.Timeline | null>(null);
  const kill = () => {
    rewind.current?.kill();
    tl.current?.kill();
    rewind.current = tl.current = null;
    extraKill?.();
  };
  useEffect(() => () => kill(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    kill,
    reset() {
      kill();
      rewind.current = resetTo();
    },
    play() {
      rewind.current?.progress(1).kill();
      tl.current = build();
      tl.current.play(0);
      return tl.current.duration();
    },
    rest() {
      const t = tl.current ?? build();
      rewind.current?.kill();
      t.progress(1).kill();
      kill();
    },
  };
}

const money = (n: number) => `${n < 0 ? "-" : "+"}$${Math.abs(n).toFixed(2).replace(".", ",")}`;

/* ---------------------------------------------------------------- shared bits */

function Icon({ src, size, className }: { src: { src: string }; size: number; className?: string }) {
  return <img src={src.src} alt="" width={size} height={size} className={cn("shrink-0", className)} style={{ width: size, height: size }} />;
}

function Cursor({ ref, tipRef, className }: { ref?: React.Ref<HTMLDivElement>; tipRef?: React.Ref<HTMLDivElement>; className?: string }) {
  return (
    <div ref={ref} className={cn("pointer-events-none absolute flex h-[39.073px] w-[39.672px] items-center justify-center", className)}>
      <div ref={tipRef} className="relative h-[31.11px] w-[32.008px] rotate-[-16.91deg] overflow-clip">
        <div className="absolute left-[calc(50%+.71px)] top-[calc(50%+.05px)] h-[19.243px] w-[18.095px] -translate-x-1/2 -translate-y-1/2">
          <img src={pointer.src} alt="" className="absolute block max-w-none" style={{ left: "-8.16%", top: "-2.45%", width: "116.32%", height: "116.66%" }} />
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- AI chat */

/** Welcome reveals, the chip is clicked and typed into the composer, sent, then the AI thinks. */
function AiChatPanel({ ref }: { ref: React.Ref<PanelHandle> }) {
  const welcome = useRef<HTMLDivElement>(null);
  const logo = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLParagraphElement>(null);
  const chip = useRef<HTMLDivElement>(null);
  const cursor = useRef<HTMLDivElement>(null);
  const cursorTip = useRef<HTMLDivElement>(null);
  const placeholder = useRef<HTMLParagraphElement>(null);
  const typed = useRef<HTMLParagraphElement>(null);
  const typedText = useRef<HTMLSpanElement>(null);
  const caret = useRef<HTMLSpanElement>(null);
  const send = useRef<HTMLDivElement>(null);
  const messages = useRef<HTMLDivElement>(null);
  const bubble = useRef<HTMLDivElement>(null);
  const aiRow = useRef<HTMLDivElement>(null);
  const dots = useRef<HTMLDivElement>(null);
  const answer = useRef<HTMLDivElement>(null);
  const replyIcon = useRef<HTMLDivElement>(null);
  const loader = useRef<gsap.core.Timeline | null>(null);

  const setTyped = (s: string) => {
    if (typedText.current) typedText.current.textContent = s;
  };
  const words = () => Array.from(title.current?.querySelectorAll("[data-word]") ?? []);
  const stopLoader = () => {
    loader.current?.kill();
    loader.current = null;
    gsap.set(dots.current?.children ?? [], { opacity: (i: number) => DOT_OPACITY[i], scale: 1 });
  };

  const panel = usePanelTimeline(
    () => {
      const typing = { n: 0 };
      const tl = gsap.timeline({ paused: true });
      tl.to(logo.current, { autoAlpha: 1, scale: 1, duration: 0.4, ease: "back.out(1.6)" }, 0)
        // same mask reveal as the LP titles (RevealWords)
        .to(words(), { yPercent: 0, duration: 1.1, ease: "expo.out", stagger: 0.09 }, 0.1)
        .to(chip.current, { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.3)
        // A2 · cursor glides onto the chip, it lights up, click
        .to(cursor.current, { autoAlpha: 1, x: 0, y: 0, duration: 0.6, ease: "power2.inOut" }, 0.8)
        .to(chip.current, { backgroundColor: "rgba(255,255,255,.06)", borderColor: "rgba(217,249,185,.6)", duration: 0.2 }, 1.3)
        .to(cursorTip.current, { scale: 0.85, duration: 0.1, yoyo: true, repeat: 1, ease: "power1.inOut" }, 1.45)
        // A3 · placeholder out, chip text typed at 30 ms per character
        .to(placeholder.current, { opacity: 0, duration: 0.15 }, 1.7)
        .to(chip.current, { opacity: 0.4, backgroundColor: "rgba(255,255,255,0)", borderColor: "rgba(255,255,255,.1)", duration: 0.3 }, 1.75)
        .to(cursor.current, { autoAlpha: 0, duration: 0.3 }, 1.75)
        .set(caret.current, { opacity: 1 }, 1.75)
        .to(typing, { n: PROMPT.length, duration: PROMPT.length * 0.03, ease: "none", onUpdate: () => setTyped(PROMPT.slice(0, Math.round(typing.n))) }, 1.75)
        .to(caret.current, { opacity: 0, duration: 0.25, ease: "steps(1)", repeat: 2, yoyo: true }, 1.75)
        // A4 · send pulses, the message moves up as a bubble, the composer clears
        .to(send.current, { keyframes: [{ scale: 0.9, duration: 0.12 }, { scale: 1, duration: 0.2 }], ease: "power2.out" }, 2.45)
        .to(typed.current, { autoAlpha: 0, y: -12, duration: 0.25, ease: "power2.in" }, 2.55)
        .to(welcome.current, { autoAlpha: 0, y: -12, duration: 0.3, ease: "power2.in" }, 2.55)
        .set(messages.current, { autoAlpha: 1 }, 2.55)
        .fromTo(bubble.current, { autoAlpha: 0, y: 90 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power3.out" }, 2.6)
        .call(() => setTyped(""), [], 2.85)
        .to(placeholder.current, { opacity: 0.5, duration: 0.3 }, 2.85)
        // A5 · orb with the 3-dot loader
        .fromTo(aiRow.current, { autoAlpha: 0, y: 8 }, { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" }, 3.05)
        .call(
          () => {
            stopLoader();
            const items = dots.current?.children ?? [];
            gsap.set(items, { opacity: 0.3 });
            loader.current = gsap
              .timeline({ repeat: -1 })
              .to(items, { keyframes: { opacity: [0.3, 1, 0.3], scale: [1, 1.15, 1] }, duration: 0.8, ease: "sine.inOut", stagger: 0.2 });
          },
          [],
          3.1,
        )
        // A6 · loader gives way to the answer, streaming in line by line
        .call(stopLoader, [], 3.1 + THINK)
        .to(dots.current, { autoAlpha: 0, duration: 0.2 }, 3.1 + THINK)
        .set(answer.current, { autoAlpha: 1 }, 3.1 + THINK)
        .to(answer.current?.children ?? [], { clipPath: "inset(0 0% 0 0)", duration: 0.8, ease: "none", stagger: 0.9 }, 3.2 + THINK);
      Array.from(answer.current?.children ?? []).forEach((line, i) => {
        tl.to(replyIcon.current, {
          y: () => (line as HTMLElement).offsetTop + ((line as HTMLElement).offsetHeight - 29.738) / 2,
          duration: 0.3,
          ease: "power2.out",
        }, 3.2 + THINK + i * 0.9);
      });
      return tl;
    },
    () =>
      gsap
        .timeline()
        .to(messages.current, { autoAlpha: 0, duration: RESET }, 0)
        .to(welcome.current, { autoAlpha: 1, y: 0, duration: RESET }, 0)
        .to(logo.current, { autoAlpha: 0, scale: 0.8, duration: RESET }, 0)
        .to(words(), { yPercent: 105, duration: RESET, ease: "power2.in" }, 0)
        .to(chip.current, { autoAlpha: 0, y: 8, backgroundColor: "rgba(255,255,255,0)", borderColor: "rgba(255,255,255,.1)", duration: RESET }, 0)
        .to(cursor.current, { autoAlpha: 0, x: 70, y: 60, duration: RESET }, 0)
        .to(placeholder.current, { opacity: 0.5, duration: RESET }, 0)
        .set(typed.current, { autoAlpha: 1, y: 0 }, 0)
        .set(caret.current, { opacity: 0 }, 0)
        .call(() => setTyped(""), [], 0)
        .set(replyIcon.current, { y: 0 }, RESET)
        .set(dots.current, { autoAlpha: 1 }, RESET)
        .set(answer.current, { autoAlpha: 0 }, RESET)
        .set(answer.current?.children ?? [], { clipPath: "inset(0 100% 0 0)" }, RESET),
    stopLoader,
  );

  useImperativeHandle(
    ref,
    () => ({
      reset: panel.reset,
      play: panel.play,
      // the chat rests on its welcome screen, not on the thinking frame
      rest() {
        panel.kill();
        gsap
          .timeline()
          .to(messages.current, { autoAlpha: 0, duration: RESET }, 0)
          .to(welcome.current, { autoAlpha: 1, y: 0, duration: RESET }, 0)
          .to(logo.current, { autoAlpha: 1, scale: 1, duration: RESET }, 0)
          .to(words(), { yPercent: 0, duration: RESET }, 0)
          .to(chip.current, { autoAlpha: 1, y: 0, backgroundColor: "rgba(255,255,255,0)", borderColor: "rgba(255,255,255,.1)", duration: RESET }, 0)
          .to(cursor.current, { autoAlpha: 0, duration: RESET }, 0)
          .to(placeholder.current, { opacity: 0.5, duration: RESET }, 0)
          .to(typed.current, { autoAlpha: 0, duration: RESET }, 0)
          .set(caret.current, { opacity: 0 }, RESET)
          .set(typed.current, { autoAlpha: 1, y: 0 }, RESET)
          .call(() => setTyped(""), [], RESET);
      },
    }),
    [panel],
  );

  return (
    <div className="relative flex h-full w-full flex-col items-center overflow-clip rounded-[8px] bg-[#0F0F12]">
      <div className="flex w-full items-center justify-between px-[18.733px] py-[14.05px]">
        <div className="flex items-center gap-[9.366px] opacity-80">
          <p className="whitespace-nowrap text-[19px] text-white">Neuer Chat</p>
          <Icon src={chevronDown} size={26} />
        </div>
        <Icon src={maximize} size={22} className="opacity-80" />
      </div>
      <div className="h-[2.342px] w-[348.9px] shrink-0 bg-[#D9D9D9] opacity-5" />

      <div className="relative h-[192.954px] w-full shrink-0">
        <div ref={welcome} className="flex w-full flex-col items-center">
          <div className="flex flex-col items-center gap-[14.686px] py-[22.028px]">
            <div ref={logo}>
              <Icon src={aiLogo} size={66.085} />
            </div>
            <p ref={title} className="whitespace-nowrap text-[20px] font-medium text-white">
              {GREETING.map((word, i) => (
                <React.Fragment key={word}>
                  {i > 0 && " "}
                  <span className="inline-block overflow-hidden align-top">
                    <span data-word className="inline-block">
                      {word}
                    </span>
                  </span>
                </React.Fragment>
              ))}
            </p>
          </div>
          <div className="relative flex w-[225.063px] flex-wrap items-start justify-center px-[12.084px]">
            <div
              ref={chip}
              className="flex shrink-0 items-center justify-center gap-[9.063px] rounded-[6.042px] border-[.755px] border-white/10 px-[12.084px] py-[9.063px]"
            >
              <Icon src={idea} size={15.105} />
              <p className="whitespace-nowrap text-[18.733px] text-white opacity-80">{PROMPT}</p>
            </div>
            {/* storyboard A2: cursor resting on the chip */}
            <Cursor ref={cursor} tipRef={cursorTip} className="invisible left-[149.36px] top-[17.73px] opacity-0" />
          </div>
        </div>

        {/* storyboard A4–A6: sent message, thinking, answer */}
        <div ref={messages} className="invisible absolute inset-0 flex flex-col gap-[16px] p-[18px] opacity-0">
          <div className="flex w-full justify-end">
            <div ref={bubble} className="flex h-[44px] items-center justify-center rounded-[8px] bg-white/[.08] px-[14px]">
              <p className="whitespace-nowrap text-[18px] leading-[24px] text-white">{PROMPT}</p>
            </div>
          </div>
          <div ref={aiRow} className="flex items-start gap-[10px]">
            <div ref={replyIcon} className="shrink-0">
              <Icon src={aiOrb} size={29.738} />
            </div>
            <div className="relative">
              <div ref={dots} className="absolute left-0 top-[10.869px] flex items-center gap-[6px]">
                {DOT_OPACITY.map((o) => (
                  <span key={o} className="h-[8px] w-[8px] rounded-full bg-white" style={{ opacity: o }} />
                ))}
              </div>
              <div ref={answer} className="invisible flex flex-col items-start gap-[8px] pt-[6px] opacity-0">
                {ANSWER_LINES.map((text) => (
                  <p key={text} className="rounded-[6px] bg-white/[.08] px-[9px] py-[5px] text-[16px] leading-[21px] text-white">
                    {text}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-auto flex h-[119.422px] w-full shrink-0 flex-col justify-end p-[18.733px]">
        <div className="flex w-full items-center justify-between rounded-[9.366px] bg-[#020203] p-[9.366px]">
          <div className="flex min-w-0 items-center gap-[9.366px]">
            <Icon src={addLarge} size={28.099} />
            <div className="relative">
              <p ref={placeholder} className="whitespace-nowrap text-[18.733px] text-white opacity-50">
                Frag zum Trading
              </p>
              <p ref={typed} className="absolute left-0 top-0 flex items-center whitespace-nowrap text-[18.733px] text-white">
                <span ref={typedText} />
                <span ref={caret} className="ml-[2px] inline-block h-[20px] w-[1.5px] bg-white opacity-0" />
              </p>
            </div>
          </div>
          <div className="flex items-center gap-[9.366px]">
            <Icon src={microphone} size={28.099} />
            <div
              ref={send}
              className="flex h-[37.466px] w-[37.466px] items-center justify-center rounded-full bg-[linear-gradient(180deg,#668744_0%,#D4F5B5_135.94%)]"
            >
              <Icon src={arrowUp} size={18.733} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- chart */

const CHART_W = 464;
const CHART_H = 395;
const LINE_X = 15.08; // line B left / width inside the 464 composition
const LINE_W = 433.888;
const LINE_A_X = 124.54;
const LINE_A_W = 110.588;
const DRAW = 1.2;
const DRAW_AT = 0.1;

/** Time (s, timeline) at which the power2.out line draw reaches composition x. */
const drawReaches = (x: number) => DRAW_AT + DRAW * (1 - Math.cbrt(1 - (x - LINE_X) / LINE_W));
const clipRight = (p: number) => `inset(-5% ${(1 - Math.min(1, Math.max(0, p))) * 105 - 5}% -5% -5%)`;

/** C1 bars grow · C2 line draws, price counts · C3 column, cursor, dot pulse, tooltip. */
function ChartPanel({ ref }: { ref: React.Ref<PanelHandle> }) {
  const price = useRef<HTMLSpanElement>(null);
  const toggle = useRef<HTMLSpanElement>(null);
  const jahr = useRef<HTMLSpanElement>(null);
  const monat = useRef<HTMLSpanElement>(null);
  const lineARef = useRef<HTMLDivElement>(null);
  const lineBRef = useRef<HTMLDivElement>(null);
  const bars = useRef<HTMLDivElement>(null);
  const column = useRef<HTMLDivElement>(null);
  const markerPill = useRef<HTMLDivElement>(null);
  const markerLine = useRef<HTMLDivElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const cursor = useRef<HTMLDivElement>(null);
  const dot = useRef<HTMLImageElement>(null);
  const draw = useRef({ p: 1, v: PRICE });

  const render = () => {
    const { p, v } = draw.current;
    const x = LINE_X + LINE_W * p;
    if (lineBRef.current) lineBRef.current.style.clipPath = clipRight(p);
    if (lineARef.current) lineARef.current.style.clipPath = clipRight((x - LINE_A_X) / LINE_A_W);
    if (price.current) price.current.textContent = Math.round(v).toLocaleString("en-US");
  };
  // active pill offset/width over "Monat" vs "Jahr"
  const pillAt = (el: HTMLElement | null) => ({ x: (el?.offsetLeft ?? 0) - (jahr.current?.offsetLeft ?? 0), width: el?.offsetWidth ?? 0 });

  const panel = usePanelTimeline(
    () =>
      gsap
        .timeline({ paused: true })
        .to(toggle.current, { ...pillAt(jahr.current), duration: 0.3, ease: "power2.out" }, 0)
        .to(jahr.current, { opacity: 1, duration: 0.3 }, 0)
        .to(monat.current, { opacity: 0.5, duration: 0.3 }, 0)
        .to(bars.current?.children ?? [], { scaleY: 1, duration: 0.4, ease: "power2.out", stagger: 0.02 }, 0)
        .to(draw.current, { p: 1, duration: DRAW, ease: "power2.out", onUpdate: render }, DRAW_AT)
        .to(draw.current, { v: PRICE, duration: 1, ease: "power2.out", onUpdate: render }, DRAW_AT)
        .to(dot.current, { autoAlpha: 1, scale: 1, duration: 0.25, ease: "back.out(2)" }, drawReaches(171.5))
        // light column when the line reaches it
        .to([column.current, markerPill.current], { autoAlpha: 1, duration: 0.4 }, drawReaches(127.1))
        .to(markerLine.current, { scaleY: 1, duration: 0.5, ease: "power2.out" }, drawReaches(127.1))
        .to(cursor.current, { autoAlpha: 1, x: 0, y: 0, duration: 0.6, ease: "power2.inOut" }, 0.9)
        .to(dot.current, { scale: 1.9, duration: 0.18, yoyo: true, repeat: 1, ease: "power1.inOut" }, 1.5)
        .to(tooltip.current, { autoAlpha: 1, y: 0, duration: 0.35, ease: "power2.out" }, 1.9),
    () =>
      gsap
        .timeline()
        .to(toggle.current, { ...pillAt(monat.current), duration: RESET }, 0)
        .to(jahr.current, { opacity: 0.5, duration: RESET }, 0)
        .to(monat.current, { opacity: 1, duration: RESET }, 0)
        .to(bars.current?.children ?? [], { scaleY: 0, duration: RESET }, 0)
        .to(draw.current, { p: 0, v: 0, duration: RESET, ease: "power2.in", onUpdate: render }, 0)
        .to([column.current, markerPill.current, dot.current], { autoAlpha: 0, duration: RESET }, 0)
        .to(dot.current, { scale: 0, duration: RESET }, 0)
        .to(markerLine.current, { scaleY: 0, duration: RESET }, 0)
        .to(cursor.current, { autoAlpha: 0, x: 70, y: 60, duration: RESET }, 0)
        .to(tooltip.current, { autoAlpha: 0, y: 6, duration: RESET }, 0),
  );
  useImperativeHandle(ref, () => panel, [panel]);

  // the active pill is its own layer so it can slide; size it to "Jahr" once fonts are in
  useLayoutEffect(() => {
    const size = () => gsap.set(toggle.current, pillAt(jahr.current));
    size();
    document.fonts?.ready.then(size);
  }, []);

  return (
    <div className="relative h-full w-full overflow-clip rounded-[8px] border-[.943px] border-white/[.06] bg-[linear-gradient(180deg,#000_0%,#2C3A1E_100%)] backdrop-blur-[7.542px]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-20 mix-blend-soft-light"
        style={{ backgroundImage: `url(${noise.src})`, backgroundSize: "386.138px 386.138px" }}
      />
      <div className="absolute left-0 top-0 flex flex-col gap-[19px] px-[15.084px] pb-[16px] pt-[20px]" style={{ width: CHART_W, height: CHART_H }}>
        <div className="relative flex w-full items-end justify-between">
          <div className="flex items-baseline gap-[9.468px] whitespace-nowrap font-medium">
            <p className="text-[24px] tabular-nums text-white">
              <span className="text-white/50">$</span>
              <span ref={price}>{PRICE.toLocaleString("en-US")}</span>
            </p>
            <p className="text-[16px] text-[#D9F9B9]">+24,5%</p>
          </div>
          <div className="relative flex items-center gap-[3.771px] rounded-[11.313px] p-[3.771px] font-roboto text-[14px] text-white">
            <span ref={toggle} className="absolute inset-y-[3.771px] left-[3.771px] rounded-[8px] bg-white/[.09]" />
            <span ref={jahr} className="relative px-[14px] py-[7.542px]">
              Jahr
            </span>
            <span ref={monat} className="relative p-[7.542px] opacity-50">
              Monat
            </span>
          </div>
        </div>

        {/* dotted grid */}
        <div
          className="absolute left-[15.08px] top-[107.58px] h-[239.979px] w-[433.888px]"
          style={{ backgroundImage: `url(${dotGrid.src})`, backgroundSize: "28.96px 28.96px", opacity: 0.08 }}
        />
        {/* quarters */}
        <div className="absolute left-[15.08px] top-[357.12px] flex h-[22.324px] w-[433.888px] items-end gap-[7.542px]">
          {["Q1", "Q2", "Q3", "Q4"].map((q) => {
            const on = q === "Q2";
            return (
              <div key={q} className="flex min-w-[1px] flex-1 flex-col justify-end gap-[7.542px]">
                <div className={cn("w-full bg-white", on ? "h-[1.885px] opacity-70" : "h-[.943px] opacity-10")} />
                <p className={cn("w-full text-[14px] font-medium text-white", on ? "opacity-70" : "opacity-30")}>{q}</p>
              </div>
            );
          })}
        </div>
        {/* months */}
        <div className="absolute left-[15.08px] top-[82.06px] flex h-[25.513px] w-[433.888px] items-end opacity-40">
          {MONTHS.map((month) => (
            <div key={month} className="flex min-w-[1px] flex-1 justify-center p-[7.542px] backdrop-blur-[7.542px]">
              <p className="whitespace-nowrap text-center text-[11.313px] uppercase text-white">{month}</p>
            </div>
          ))}
        </div>
        {/* chart lines */}
        <div ref={lineARef} className="absolute left-[124.54px] top-[143.29px] h-[115.955px] w-[110.588px]">
          <img src={lineA.src} alt="" className="absolute bottom-[-2.09%] left-[-2.2%] right-[-2.14%] top-[-.6%] block max-w-none" style={{ width: "104.34%", height: "102.69%" }} />
        </div>
        <div ref={lineBRef} className="absolute left-[15.08px] top-[113.13px] h-[212.112px] w-[433.888px]">
          <img src={lineB.src} alt="" className="absolute left-0 top-[-.28%] block max-w-none" style={{ width: "100%", height: "100.42%" }} />
        </div>
        {/* hovered month column */}
        <div
          ref={column}
          className="pointer-events-none absolute left-[127.1px] top-[102px] h-[245px] w-[102px] shadow-[inset_0px_-30.167px_37.709px_0px_rgba(217,249,185,0.03),inset_0px_-49.021px_116.897px_0px_rgba(217,249,185,0.05)]"
          style={{
            backgroundImage:
              "linear-gradient(180deg, rgba(217,249,185,0) 0%, rgba(217,249,185,.05) 100%), linear-gradient(90deg, rgba(255,255,255,.02) 0%, rgba(255,255,255,.02) 100%)",
          }}
        />
        {/* volume */}
        <div ref={bars} className="absolute left-[15.08px] top-[334px] flex h-[16.743px] w-[433.888px] items-end gap-[.943px]">
          {BARS.map(([h, o, w], i) => (
            <div
              key={i}
              className={cn("origin-bottom bg-[#D9F9B9]", w ? "shrink-0" : "min-w-[1px] flex-1")}
              style={{ height: h ?? "100%", opacity: o, width: w }}
            />
          ))}
        </div>
        {/* tooltip */}
        <div
          ref={tooltip}
          className="absolute left-[180px] top-[126px] flex h-[66.933px] w-[171.088px] flex-col gap-[7.542px] overflow-clip whitespace-nowrap rounded-[7.542px] border-[.943px] border-white/[.06] bg-white/[.08] p-[11.313px] backdrop-blur-[7.542px]"
        >
          <p className="text-[14px] text-white opacity-70">May 2026</p>
          <div className="flex items-center gap-[7.542px] font-medium">
            <div className="flex items-center gap-[1.885px] text-white">
              <p className="text-[15.084px] opacity-40">$</p>
              <p className="text-[16px]">16,735</p>
            </div>
            <p className="text-[14px] text-[#D9F9B9]">+15,5%</p>
          </div>
        </div>
        {/* MAY marker */}
        <div className="absolute left-[160.1px] top-[81px] flex h-[273.245px] w-[23.407px] flex-col items-center justify-center gap-[3.771px]">
          <div
            ref={markerPill}
            className="flex items-start rounded-full border-[.943px] border-[#D9F9B9] px-[7.542px] py-[3.771px] backdrop-blur-[7.542px]"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(217,249,185,.05) 0%, rgba(217,249,185,.05) 100%), linear-gradient(174.84deg, rgba(39,43,49,.4) 0%, rgba(24,28,33,.4) 93.533%), linear-gradient(90deg, rgba(255,255,255,.03) 0%, rgba(255,255,255,.03) 100%)",
            }}
          >
            <p className="whitespace-nowrap text-center text-[11.313px] uppercase text-white">MAY</p>
          </div>
          <div ref={markerLine} className="flex min-h-[1px] flex-1 origin-top flex-col items-center">
            <Icon src={markerDot} size={4.714} />
            <div className="min-h-[1px] w-[.943px] flex-1 bg-[#D9D9D9]" />
          </div>
        </div>
        <Cursor ref={cursor} className="left-[159px] top-[194px]" />
        <img ref={dot} src={pointDot.src} alt="" className="absolute left-[168.16px] top-[201.14px] h-[6.682px] w-[6.682px]" />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- journal */

const PILL_RGB = { win: "0,209,111", loss: "255,100,121" };

/** J1 tabs slide to 30d · J2 rows enter · J3 P&L pills count up and flash. */
function JournalPanel({ ref }: { ref: React.Ref<PanelHandle> }) {
  const indicator = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLDivElement>(null);
  const count = useRef<HTMLParagraphElement>(null);
  const rows = useRef<HTMLDivElement>(null);
  const values = useRef(TRADES.map(([v]) => ({ v })));
  const counter = useRef({ v: TRADE_COUNT });

  const pills = () => Array.from(rows.current?.querySelectorAll<HTMLElement>("[data-pill]") ?? []);
  const renderPill = (i: number) => {
    const el = pills()[i]?.firstElementChild;
    if (el) el.textContent = money(values.current[i].v);
  };
  const renderCount = () => {
    if (count.current) count.current.textContent = String(Math.round(counter.current.v));
  };

  const panel = usePanelTimeline(
    () => {
      const tl = gsap
        .timeline({ paused: true })
        .to(indicator.current, { xPercent: 0, duration: 0.3, ease: "power2.out" }, 0)
        .to(header.current, { autoAlpha: 1, duration: 0.3 }, 0.1)
        .to(rows.current?.children ?? [], { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out", stagger: 0.06 }, 0.3)
        // header counter runs with the rows
        .to(counter.current, { v: TRADE_COUNT, duration: 0.6, ease: "power1.out", onUpdate: renderCount }, 0.3);
      pills().forEach((pill, i) => {
        const [value] = TRADES[i];
        const rgb = value < 0 ? PILL_RGB.loss : PILL_RGB.win;
        const at = 0.3 + i * 0.06;
        tl.to(values.current[i], { v: value, duration: 0.6, ease: "power2.out", onUpdate: () => renderPill(i) }, at).to(
          pill,
          {
            keyframes: [
              { backgroundColor: `rgba(${rgb},.35)`, duration: 0.15, ease: "power1.out" },
              { backgroundColor: `rgba(${rgb},.15)`, duration: 0.35, ease: "power1.in" },
            ],
          },
          at + 0.6,
        );
      });
      return tl;
    },
    () =>
      gsap
        .timeline()
        .to(indicator.current, { xPercent: 200, duration: RESET }, 0)
        .to([header.current, ...(rows.current?.children ?? [])], { autoAlpha: 0, duration: RESET }, 0)
        .to(rows.current?.children ?? [], { y: 8, duration: RESET }, 0)
        .call(
          () => {
            counter.current.v = 0;
            renderCount();
            values.current.forEach((o, i) => {
              o.v = 0;
              renderPill(i);
            });
          },
          [],
          RESET,
        ),
  );
  useImperativeHandle(ref, () => panel, [panel]);

  return (
    <div className="flex h-full w-full flex-col overflow-clip rounded-[8px] bg-[#252525]">
      <div className="h-[2.387px] w-[390.29px] shrink-0 bg-[#D9D9D9] opacity-5" />
      <div className="flex w-full flex-col gap-[19.097px] py-[19.097px]">
        <div className="flex w-full flex-col gap-[4.774px] px-[19.097px]">
          <p className="w-full text-[22.79px] text-white">Zeitraum</p>
          <div className="relative flex w-full items-center overflow-clip rounded-[4px] border-[1.194px] border-white/10 text-[19.92px] text-white">
            <div ref={indicator} className="absolute inset-y-0 left-0 w-1/3 bg-[#1A1A1C]" />
            <span className="relative flex min-w-[1px] flex-1 items-center justify-center p-[11.385px]">30d</span>
            <span className="relative flex min-w-[1px] flex-1 items-center justify-center border-l-[1.423px] border-white/10 p-[11.385px]">
              <span className="opacity-50">3m</span>
            </span>
            <span className="relative flex min-w-[1px] flex-1 items-center justify-center border-l-[1.423px] border-white/10 p-[11.385px]">
              <span className="opacity-50">Gesamt</span>
            </span>
          </div>
        </div>
        <div className="h-[1.194px] w-full shrink-0 bg-[#D9D9D9] opacity-5" />
        <div className="flex w-full flex-col gap-[9.548px]">
          <div ref={header} className="flex w-full items-center justify-between whitespace-nowrap px-[19.097px] text-white">
            <p className="text-[19.097px]">Letzte Trades</p>
            <p ref={count} className="font-geistmono text-[16.71px] font-medium tabular-nums opacity-50">
              {TRADE_COUNT}
            </p>
          </div>
          <div ref={rows} className="flex w-full flex-col">
            {TRADES.map(([value, label], i) => (
              <div
                key={i}
                className="flex w-full items-center justify-between border-b-[1.194px] border-white/5 px-[19.097px] py-[9.548px] font-geistmono text-[16.71px] uppercase leading-[1.5]"
              >
                <p className="whitespace-nowrap text-white opacity-50">btcusd</p>
                <div className="flex items-center gap-[28.645px]">
                  <p className="whitespace-nowrap text-white opacity-50">13h 56m</p>
                  {/* the pill hugs its value; the fixed slot keeps the time column in place */}
                  <div className="flex w-[95.484px] justify-end">
                    <div
                      data-pill
                      className={cn(
                        "flex items-center justify-center px-[4.774px] py-[2.387px] tabular-nums",
                        value >= 0 ? "bg-[rgba(0,209,111,.15)] text-[#00D16F]" : "bg-[rgba(255,100,121,.15)] text-[#FF6479]",
                      )}
                    >
                      <p className="whitespace-nowrap">{label}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- carousel */

const mod = (n: number, m: number) => ((n % m) + m) % m;

/** Track card i relative to the centre card: 0 centre, -1 left, 1 right, -2 / 2 / 3 off-board. */
const relSlot = (i: number, centre: number) => mod(i - centre + 2, TRACK.length) - 2;

/** Card transform: centre at full size, sides scaled to SIDE_W, the rest parked off-board. */
function place(i: number, centre: number) {
  const [w, h] = SIZE[TRACK[i]];
  const r = relSlot(i, centre);
  const first = SIZE[TRACK[centre]][0] / 2 + GAP + SIDE_W / 2; // centre → neighbour, centre to centre
  const cx = BOARD_W / 2 + (r === 0 ? 0 : Math.sign(r) * (first + (Math.abs(r) - 1) * (SIDE_W + GAP)));
  return { x: cx - w / 2, y: BOARD_H / 2 - 6 - h / 2, scale: r === 0 ? 1 : SIDE_W / w };
}

/**
 * Card 01: AI chat, chart and journal run through the centre as a carousel (right → left),
 * each playing its own inner animation. Phones show the same board scaled to the card width
 * (Figma mobile 12818:174184). `height` (board px) makes the board taller with the cards kept
 * in the middle (replay page).
 */
export function ToolsVisual({ className, height = BOARD_H }: { className?: string; height?: number }) {
  const boardRef = useRef<HTMLDivElement>(null);
  const cards = useRef<Record<number, HTMLDivElement | null>>({});
  const panels = useRef<Record<number, PanelHandle | null>>({});
  const advance = useRef<gsap.core.Tween | null>(null);
  const startCall = useRef<gsap.core.Tween | null>(null);
  const live = useRef({ centre: FIRST, inView: false, started: false });

  const veil = (i: number) => cards.current[i]?.querySelector("[data-veil]") ?? null;
  const clearTimers = () => {
    advance.current?.kill();
    startCall.current?.kill();
    advance.current = startCall.current = null;
  };

  // centre card arrived: inner animation after START, advance once it has finished + its HOLD
  const arrive = (i: number) => {
    clearTimers();
    startCall.current = gsap.delayedCall(START, () => {
      const length = panels.current[i]?.play() ?? 0;
      advance.current = gsap.delayedCall(length + HOLD[TRACK[i]], () => go(live.current.centre + 1));
      if (!live.current.inView) advance.current.pause();
    });
  };

  const go = (to: number) => {
    const from = live.current.centre;
    const next = mod(to, TRACK.length);
    if (next === from) return;
    clearTimers();
    panels.current[from]?.rest();
    panels.current[next]?.reset();
    live.current.centre = next;

    TRACK.forEach((_, i) => {
      const el = cards.current[i];
      const before = relSlot(i, from);
      const after = relSlot(i, next);
      const target = place(i, next);
      gsap.killTweensOf([el, veil(i)]);
      // wrapped from one end of the track to the other: both ends are off-board, jump unseen
      if (Math.abs(after - before) > 1) {
        gsap.set(el, target);
        gsap.set(veil(i), { opacity: SIDE_VEIL });
        return;
      }
      gsap.to(el, { ...target, duration: SLIDE, ease: SLIDE_EASE, onComplete: i === next ? () => arrive(i) : undefined });
      gsap.to(veil(i), { opacity: after === 0 ? 0 : SIDE_VEIL, duration: SLIDE, ease: SLIDE_EASE });
    });
  };

  // place every card for the current centre (gsap owns transforms/opacity from here on)
  useLayoutEffect(() => {
    const c = live.current.centre;
    TRACK.forEach((_, i) => {
      const el = cards.current[i];
      gsap.killTweensOf([el, veil(i)]);
      gsap.set(el, place(i, c));
      gsap.set(veil(i), { opacity: i === c ? 0 : SIDE_VEIL });
    });
  }, []);

  // start on first view, pause off-screen
  useEffect(() => {
    const el = boardRef.current;
    if (!el || prefersReducedMotion()) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        live.current.inView = entry.isIntersecting;
        if (entry.isIntersecting && !live.current.started) {
          live.current.started = true;
          panels.current[live.current.centre]?.reset();
          arrive(live.current.centre);
        }
        if (entry.isIntersecting) advance.current?.resume();
        else advance.current?.pause();
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearTimers();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Artboard width={BOARD_W} height={height} className={cn("bg-[#F2F2F2]", className)}>
      {/* the fill sits inside the scaled group so the soft-light grain blends against it */}
      <div ref={boardRef} className="relative h-full w-full bg-[#F2F2F2] font-geist">
        <div
          className="absolute bottom-[2.23%] left-[13.46%] right-[24.83%] top-[14.36%] opacity-[.46] mix-blend-soft-light"
          style={{
            backgroundImage: `url(${grain01.src}), linear-gradient(90deg, #808080 0%, #808080 100%)`,
            backgroundSize: "305.851px 305.851px, auto",
          }}
        />
        <div className="absolute left-[calc(50%-1px)] top-[calc(50%-.5px)] h-[536px] w-[703px] -translate-x-1/2 -translate-y-1/2 overflow-hidden border border-[#5D5796] opacity-[.28] mix-blend-soft-light">
          <img src={grain.src} alt="" className="absolute left-[-22.17%] top-[-6.9%] h-[106.9%] w-[144.9%] max-w-none" />
        </div>

        {/* the carousel keeps its 511 track, centred on a taller board */}
        <div className="absolute inset-x-0" style={{ top: (height - BOARD_H) / 2, height: BOARD_H }}>
          {TRACK.map((id, i) => {
            // server render / first paint: state 01, gsap takes over on mount
            const { x, y, scale } = place(i, FIRST);
            const setHandle = (handle: PanelHandle | null) => void (panels.current[i] = handle);
            return (
              <div
                key={i}
                ref={(el) => {
                  cards.current[i] = el;
                }}
                aria-hidden={i >= PANELS.length || undefined}
                className="absolute left-0 top-0 origin-center"
                style={{ width: SIZE[id][0], height: SIZE[id][1], transform: `translate(${x}px, ${y}px) scale(${scale})` }}
              >
                {id === "chat" && <AiChatPanel ref={setHandle} />}
                {id === "chart" && <ChartPanel ref={setHandle} />}
                {id === "journal" && <JournalPanel ref={setHandle} />}
                <div
                  data-veil
                  className="pointer-events-none absolute inset-0 rounded-[8px] bg-[#F2F2F2]"
                  style={{ opacity: i === FIRST ? 0 : SIDE_VEIL }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Artboard>
  );
}
